// /api/leads — job leads parsed from inbound email (job alerts, recruiters).
// Drives the Home LeadsTile.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export interface JobLead {
  id: string;
  source: string;
  external_id: string | null;
  title: string | null;
  company_name: string | null;
  location: string | null;
  external_url: string | null;
  status: "new" | "matched" | "unmatched" | "dismissed";
  company_id: string | null;
  job_id: string | null;
  candidate_id: string | null;
  seen_count: number;
  received_at: number;
  job_url: string | null;
  job_title: string | null;
  score: number | null;
  application_id: string | null;
}

export interface LeadsResponse {
  leads: JobLead[];
  counts: Record<string, number>;
}

export function useLeads() {
  return useQuery({
    queryKey: ["leads"],
    queryFn: () => api<LeadsResponse>("/api/leads"),
    staleTime: 60 * 1000,
  });
}

// Optimistic removal, same shape as useMarkAlertSeen.
export function useDismissLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<{ ok: boolean }>(`/api/leads/${id}/dismiss`, { method: "POST" }),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ["leads"] });
      const prev = qc.getQueryData<LeadsResponse>(["leads"]);
      if (prev) qc.setQueryData<LeadsResponse>(["leads"], { ...prev, leads: prev.leads.filter((l) => l.id !== id) });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["leads"], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["leads"] });
    },
  });
}
